import { request, setCSRFToken, clearCSRFToken } from './client';
import type { AuthResponse, LoginCredentials, RegisterCredentials, User, UpdateProfileInput } from '../types';

// Extended auth response with CSRF token
interface AuthResponseWithCSRF extends AuthResponse {
  csrfToken: string;
}

export const authApi = {
  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    const response = await request<AuthResponseWithCSRF>('/api/squareup/auth/register', {
      method: 'POST',
      body: credentials,
      skipCsrf: true, // Registration doesn't need CSRF (no existing session)
    });
    // Store CSRF token in memory for subsequent requests
    if (response.csrfToken) {
      setCSRFToken(response.csrfToken);
    }
    return response;
  },

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await request<AuthResponseWithCSRF>('/api/squareup/auth/login', {
      method: 'POST',
      body: credentials,
      skipCsrf: true, // Login doesn't need CSRF (no existing session)
    });
    // Store CSRF token in memory for subsequent requests
    if (response.csrfToken) {
      setCSRFToken(response.csrfToken);
    }
    return response;
  },

  async me(): Promise<User> {
    return request<User>('/api/squareup/auth/me');
  },

  async updateProfile(input: UpdateProfileInput): Promise<User> {
    return request<User>('/api/squareup/auth/profile', {
      method: 'PATCH',
      body: input,
    });
  },

  async logout(): Promise<void> {
    try {
      // Call server to clear HTTP-only cookies
      await request<void>('/api/squareup/auth/logout', {
        method: 'POST',
        skipCsrf: true, // Logout should work even if CSRF token is missing
      });
    } catch {
      // Even if server call fails, clear local state
    }
    // Clear CSRF token from memory
    clearCSRFToken();
  },

  async refreshToken(): Promise<AuthResponse> {
    const response = await request<AuthResponseWithCSRF>('/api/squareup/auth/refresh', {
      method: 'POST',
    });
    // Update CSRF token
    if (response.csrfToken) {
      setCSRFToken(response.csrfToken);
    }
    return response;
  },

  async forgotPassword(email: string): Promise<void> {
    await request<void>('/api/squareup/auth/forgot-password', {
      method: 'POST',
      body: { email },
      skipCsrf: true, // Public endpoint
    });
  },

  async validateResetToken(token: string): Promise<{ valid: boolean }> {
    return request<{ valid: boolean }>('/api/squareup/auth/validate-reset-token', {
      method: 'POST',
      body: { token },
      skipCsrf: true, // Public endpoint
    });
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await request<void>('/api/squareup/auth/reset-password', {
      method: 'POST',
      body: { token, newPassword: password },
      skipCsrf: true, // Public endpoint
    });
  },

  async requestEmailChange(newEmail: string): Promise<void> {
    await request<void>('/api/squareup/auth/request-email-change', {
      method: 'POST',
      body: { newEmail },
    });
  },

  async verifyEmailChange(token: string): Promise<void> {
    await request<void>('/api/squareup/auth/verify-email-change', {
      method: 'POST',
      body: { token },
      skipCsrf: true, // Public endpoint (user clicks email link)
    });
  },

  async getPendingEmailChange(): Promise<{ pending: boolean; newEmail?: string; expiresAt?: string }> {
    const response = await request<{ hasPending: boolean; newEmail?: string; expiresAt?: string }>('/api/squareup/auth/pending-email-change');
    return { pending: response.hasPending, newEmail: response.newEmail, expiresAt: response.expiresAt };
  },

  async cancelEmailChange(): Promise<void> {
    await request<void>('/api/squareup/auth/cancel-email-change', {
      method: 'POST',
    });
  },

  async resendEmailChangeVerification(): Promise<void> {
    await request<void>('/api/squareup/auth/resend-email-change', {
      method: 'POST',
    });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await request<void>('/api/squareup/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    });
  },

  // SSO methods
  async getSSOProviders(): Promise<{ providers: SSOProvider[] }> {
    return request<{ providers: SSOProvider[] }>('/api/squareup/auth/providers', {
      skipCsrf: true, // Public endpoint
    });
  },

  async getSSOAuthURL(provider: string, state: string): Promise<{ authUrl: string }> {
    const params = new URLSearchParams({ provider, state });
    return request<{ authUrl: string }>(`/api/squareup/auth/sso/auth?${params}`, {
      skipCsrf: true, // Public endpoint
    });
  },

  async handleSSOCallback(provider: string, code: string, state?: string): Promise<SSOCallbackResponse> {
    const paramObj: Record<string, string> = { provider, code };
    if (state) paramObj.state = state;
    const params = new URLSearchParams(paramObj);
    const response = await request<SSOCallbackResponse & AuthResponseWithCSRF>(`/api/squareup/auth/sso/callback?${params}`, {
      skipCsrf: true, // Public endpoint (no existing session)
    });
    // Store CSRF token in memory for subsequent requests (only if full auth response)
    if ('csrfToken' in response && response.csrfToken) {
      setCSRFToken(response.csrfToken);
    }
    return response;
  },

  async completeSSORegistration(input: CompleteSSORegistrationInput): Promise<AuthResponse> {
    const response = await request<AuthResponseWithCSRF>('/api/squareup/auth/sso/complete-registration', {
      method: 'POST',
      body: input,
      skipCsrf: true, // No existing session
    });
    // Store CSRF token in memory for subsequent requests
    if (response.csrfToken) {
      setCSRFToken(response.csrfToken);
    }
    return response;
  },

  // Linked accounts methods
  async getLinkedAccounts(): Promise<LinkedAccounts> {
    return request<LinkedAccounts>('/api/squareup/auth/linked-accounts');
  },

  async linkGoogleAccount(code: string): Promise<User> {
    return request<User>('/api/squareup/auth/link/google', {
      method: 'POST',
      body: { code },
    });
  },

  async unlinkGoogleAccount(): Promise<User> {
    return request<User>('/api/squareup/auth/link/google', {
      method: 'DELETE',
    });
  },

  async linkFacebookAccount(code: string): Promise<User> {
    return request<User>('/api/squareup/auth/link/facebook', {
      method: 'POST',
      body: { code },
    });
  },

  async unlinkFacebookAccount(): Promise<User> {
    return request<User>('/api/squareup/auth/link/facebook', {
      method: 'DELETE',
    });
  },

  async linkTwitterAccount(code: string, state: string): Promise<User> {
    return request<User>('/api/squareup/auth/link/twitter', {
      method: 'POST',
      body: { code, state },
    });
  },

  async unlinkTwitterAccount(): Promise<User> {
    return request<User>('/api/squareup/auth/link/twitter', {
      method: 'DELETE',
    });
  },
};

// SSO Provider type
export interface SSOProvider {
  id: string;
  displayName: string;
  icon: string;
}

// Linked accounts type
export interface LinkedAccounts {
  google: boolean;
  facebook: boolean;
  twitter: boolean;
  hasPassword: boolean;
}

// SSO callback response - can be either a full auth response or a pending email response
export type SSOCallbackResponse = AuthResponse | PendingEmailResponse;

// Pending email response - returned when SSO user has no email (Facebook phone-only)
export interface PendingEmailResponse {
  requiresEmail: true;
  pendingEmailToken: string;
  displayName: string;
  provider: string;
}

// Input for completing SSO registration with email
export interface CompleteSSORegistrationInput {
  pendingEmailToken: string;
  email: string;
  tosAcceptedAt: string;
}

// Type guard to check if response requires email
export function requiresEmail(response: SSOCallbackResponse): response is PendingEmailResponse {
  return 'requiresEmail' in response && response.requiresEmail === true;
}
